import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import * as Sentry from '@sentry/react';
import cn from 'classnames';
import { $getRoot, $isTextNode } from 'lexical';
import { FC, ReactNode } from 'react';

import { EditablePlugin } from '@/shared/ui';

import type { DocumentSectionSpec } from '../../config';
import { $importTemplate, type TemplateDocumentExport } from '../../lib';
import {
  NoteFieldNamePlugin,
  NoteWidgetEventsPlugin,
  type FieldNameReading,
} from '../NoteEditor/plugins';

import {
  $isTemplateFieldNode,
  $isTemplateParagraphNode,
  TEMPLATE_EDITOR_NODES,
  TemplateFieldWidgetsContext,
  type TemplateFieldWidgets,
} from './nodes';
import {
  TemplateExportPlugin,
  TemplateKeysPlugin,
  TemplateLinkModePlugin,
  TemplateNodeStatePlugin,
  TemplateStructureLockPlugin,
  type TemplateEditorHistory,
} from './plugins';
import styles from './TemplateEditor.module.scss';
import { TEMPLATE_LINK_ADAPTER } from './templateLinkMode';

// A field's name is all the text between it and the line's start or the
// field or instruction before it.
const TEMPLATE_FIELD_NAMES: FieldNameReading = {
  $listLines: () => $getRoot().getChildren().filter($isTemplateParagraphNode),
  isField: $isTemplateFieldNode,
  isWording: $isTextNode,
  blockIdOf: () => null,
  endsName: () => true,
};

type Props = {
  // Read once, when the editor mounts: a key starts it on another template.
  sections: DocumentSectionSpec[];
  widgets: TemplateFieldWidgets;
  editable: boolean;
  ariaLabel: string;
  // A summary's or an evaluation's fields, read with `sections`: a heading
  // and an instruction each.
  fields?: boolean;
  // Only the fields' titles and instructions are typed.
  structureLocked?: boolean;
  className?: string;
  // The consumer's plugins, inside the editor.
  children?: ReactNode;
  // A section Lexical's own editing makes, as a heading cut in two.
  createSection: (title: string) => DocumentSectionSpec;
  onChange: (exported: TemplateDocumentExport) => void;
  onHistoryChange: (history: TemplateEditorHistory) => void;
};

// A template as one rich-text document: a section is a heading, a line a
// paragraph, wording its text, and a field or an AI instruction one unit in
// it. Lexical types, deletes, selects and undoes; the editor only keeps the
// ids with what they name.
export const TemplateEditor: FC<Props> = ({
  sections,
  widgets,
  editable,
  ariaLabel,
  fields = false,
  structureLocked = false,
  className,
  children,
  createSection,
  onChange,
  onHistoryChange,
}) => (
  <TemplateFieldWidgetsContext.Provider value={widgets}>
    <LexicalComposer
      initialConfig={{
        namespace: 'TemplateEditor',
        nodes: TEMPLATE_EDITOR_NODES,
        editable,
        editorState: () => $importTemplate(sections, { fields }),
        onError: (error: Error) => {
          Sentry.captureException(error);
        },
      }}
    >
      <RichTextPlugin
        contentEditable={
          <ContentEditable
            className={cn('p2', styles.editor, className)}
            aria-label={ariaLabel}
          />
        }
        ErrorBoundary={LexicalErrorBoundary}
      />
      <EditablePlugin readOnly={!editable} />
      <HistoryPlugin />
      <TemplateNodeStatePlugin fields={fields} createSection={createSection} />
      <TemplateKeysPlugin fields={fields} />
      <NoteWidgetEventsPlugin />
      {!fields && <NoteFieldNamePlugin reading={TEMPLATE_FIELD_NAMES} />}
      {structureLocked && <TemplateStructureLockPlugin />}
      {!fields && !structureLocked && (
        <TemplateLinkModePlugin adapter={TEMPLATE_LINK_ADAPTER} />
      )}
      <TemplateExportPlugin
        fields={fields}
        onChange={onChange}
        onHistoryChange={onHistoryChange}
      />
      {children}
    </LexicalComposer>
  </TemplateFieldWidgetsContext.Provider>
);
